import {Tools} from "../tools.js";

/**
 * Utility class for managing and generating option lists based on definitions, field dependencies,
 * and other criteria. This class provides static methods to handle both static and dynamic
 * options for form elements.
 */
export class OptionTools {

    /**
     * Checks to see if the option definition is based on another field value
     * @param options Options definition to inspect
     * @returns {boolean} TRUE - options list dependant on value, FALSE - options list is NOT dependant
     */
    static isDynamicOptions(options) {
        return !Array.isArray(options) && options.dependsOn !== undefined; // Checking if present, not value
    }

    /**
     * Builds the options based on the definitions
     * @param definition Element definition
     * @param options Options definition to display
     * @param formData Form Data object
     * @returns Array of options to display on form {text: string, value: any}
     */
    static buildOptions(definition, options, formData) {
        switch (true) {
            case OptionTools.isDynamicOptions(options):
                return OptionTools.getDynamicOptions(definition, options, formData);
                break;

            default:
                return OptionTools.getOptionsArray(definition, options);
        }
    }

    /**
     * Builds the options array to be displayed
     * @param definition The element definition
     * @param optionsDefinition The options to display
     * @returns Array of the options to display {text: string, value: any}
     */
    static getOptionsArray(definition, optionsDefinition) {
        const defaultValue = definition.customPlaceholder ? definition.customPlaceholder : "-- Please Select --";
        const optionsArray = [];
        let optionsDefinitionArray = [...optionsDefinition];

        if (!definition.ignorePlaceholder && definition.noValue !== false) {
            switch (true) {
                case typeof (definition.noValue ?? defaultValue) === 'string':
                    optionsDefinitionArray.unshift([definition.noValue ?? defaultValue, '']);
                    break;

                default:
                    optionsDefinitionArray.unshift(definition.noValue);
            }
        }

        for (const option of optionsDefinitionArray) {
            let value = null;
            let text = null;

            switch (true) {
                case Array.isArray(option):
                    text = option[0];
                    value = option[1];
                    break;

                case typeof option === 'string':
                    text = option;
                    break;

                case (Tools.isClass(option) || typeof option === 'object') && definition.textField !== undefined:
                    text = option[definition.textField];
                    value = option[definition.valueField];
                    break;

                default:
                    text = option.text;
                    value = option.value;
                    break;
            }
            if (text == null || text.length === 0) {
                continue;
            }

            optionsArray.push({text, value: value ?? text});          
        }

        return this.postOptionsFilter(optionsArray, definition);
    }

    /**
     * Builds the option list based on the form values that defines it
     * @param definition
     * @param options Options Definition
     * @param formData Data to get values from
     * @returns Array of the options to display {text: string, value: any}
     */
    static getDynamicOptions(definition, options, formData) {
        const field = options.dependsOn;
        let fieldValue = formData[field];
        if (fieldValue === undefined) {
            fieldValue = "[[<<UNDEFINED>>]]"
        }
        return OptionTools.getOptionsArray(definition, options.options[fieldValue] ?? []);
    }

    /**
     * Processes post options filter dependencies from the given definition object.
     *
     * @param {Object} definition - The definition object containing the post options filter information.
     * @param {Object} [definition.postOptionsFilter] - The post options filter details.
     * @param {string} [definition.postOptionsFilter.dependsOn] - The field name on which the filter depends, potentially prefixed with "form::".
     * @param {Object} [definition.parent] - The parent object containing form variables.
     * @param {Object} [definition.parent.formVariables] - Form variables of the parent.
     * @param {Object} [definition.collection] - The collection object containing parent form details.
     * @param {Object} [definition.collection.parentForm] - The parent form associated with the collection.
     * @param {Object} [definition.collection.parentForm.formVariables] - Form variables of the parent form.
     * @param {Object} [definition.formData] - The form data associated with the definition.
     *
     * @return {*} The value from the dependent field specified in the filter, or null if no valid dependency is found.
     */
    static postOptionsFilterDepends(definition) {
        if (definition?.postOptionsFilter == null) {
            return null;
        }
        const dependField = definition.postOptionsFilter.dependsOn;
        if (dependField == null) {
            return null;
        }
        const data = dependField.startsWith("form::")
            ? definition.parent?.formVariables ?? definition.collection?.parentForm?.formVariables ?? {}
            : definition.formData;

        const field = dependField.startsWith("form::") ? dependField.substring(6) : dependField;
        const value = data[field];

        return value;
    }

    /**
     * Filters the provided options array based on the specified definition's postOptionsFilter criteria.
     *
     * @param {Array} optionsArray - Array of option objects to be filtered.
     * @param {Object} definition - Object containing the postOptionsFilter criteria and dependency key.
     * @return {Array} The filtered options array, or the original array if no valid filter or key is found.
     */
    static postOptionsFilter(optionsArray, definition) {

        const filter = definition.postOptionsFilter;
        const key = OptionTools.postOptionsFilterDepends(definition);
        if (filter == null || key == null) {
            return optionsArray;
        }

        const filters = filter.filters?.[key];
        if (filters == null) {
            return optionsArray;
        }

        const filteredOptions = Array.isArray(filters)
            ? optionsArray.filter(option => filters.includes(option.value))
            : [];

        return filteredOptions;
    }
}
